import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchWorkspaceUsers,
  inviteUser,
  updateWorkspaceUser,
  removeWorkspaceUser,
  resendWorkspaceInvitation,
  WorkspaceUser,
  InviteUserRequest,
  UpdateUserRequest,
} from '@/api/workspaceUsers.api';

// Re-export types for convenience
export type { WorkspaceUser, InviteUserRequest, UpdateUserRequest };

/**
 * Hook for managing workspace users
 * 
 * Provides:
 * - List of workspace users
 * - Invite user functionality
 * - Update user role/status
 * - Remove user functionality
 */
export function useWorkspaceUsers(workspaceId: string | undefined) {
  const queryClient = useQueryClient();

  // Fetch workspace users
  const {
    data: users = [],
    isLoading,
    error,
    refetch,
  } = useQuery<WorkspaceUser[]>({
    queryKey: ['workspace-users', workspaceId],
    queryFn: async () => {
      if (!workspaceId) {
        return [];
      }
      return await fetchWorkspaceUsers(workspaceId);
    },
    enabled: !!workspaceId,
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Invite user mutation
  const inviteUserMutation = useMutation({
    mutationFn: async (data: InviteUserRequest) => {
      if (!workspaceId) {
        throw new Error('Workspace ID is required');
      }
      return await inviteUser(workspaceId, data.email, data.role, data.first_name);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-users', workspaceId] });
      // Also invalidate access queries to refresh permissions
      queryClient.invalidateQueries({ queryKey: ['workspace-access', workspaceId] });
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UpdateUserRequest }) => {
      if (!workspaceId) {
        throw new Error('Workspace ID is required');
      }
      return await updateWorkspaceUser(workspaceId, userId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-users', workspaceId] });
      // Also invalidate access queries to refresh permissions
      queryClient.invalidateQueries({ queryKey: ['workspace-access', workspaceId] });
    },
  });

  // Remove user mutation
  const removeUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!workspaceId) {
        throw new Error('Workspace ID is required');
      }
      return await removeWorkspaceUser(workspaceId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-users', workspaceId] });
      // Also invalidate access queries to refresh permissions
      queryClient.invalidateQueries({ queryKey: ['workspace-access', workspaceId] });
    },
  });

  // Resend invitation mutation
  const resendInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      if (!workspaceId) {
        throw new Error('Workspace ID is required');
      }
      return await resendWorkspaceInvitation(workspaceId, invitationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-users', workspaceId] });
    },
  });

  return {
    users,
    isLoading,
    error,
    refetch,
    inviteUser: inviteUserMutation.mutateAsync,
    updateUser: updateUserMutation.mutateAsync,
    removeUser: removeUserMutation.mutateAsync,
    resendInvitation: resendInvitationMutation.mutateAsync,
    isInviting: inviteUserMutation.isPending,
    isUpdating: updateUserMutation.isPending,
    isRemoving: removeUserMutation.isPending,
    isResendingInvitation: resendInvitationMutation.isPending,
  };
}

